import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Route as ReactDOMRoute, useHistory } from 'react-router-dom';
import { Layout, Skeleton, Result, notification } from 'antd';
import { isBefore } from 'date-fns';

import { pageView } from '../helpers/googleAnalytics';
import Cabecalho from '../components/Cabecalho';
import MenuLateral from '../components/MenuLateral';
import { useAuth } from '../hooks/auth';

const { Content, Header } = Layout;

function ContentLayout({ children }) {
  return children;
}

ContentLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

function PrivateLayout({
  children,
  titleHeader,
  info,
  carregando,
  systemVersion,
  roles,
  temPermissao,
  watch,
  ...props
}) {
  return <Layout style={{ minHeight: '100vh' }}>
    <MenuLateral carregando={carregando} {...props} />
    <Layout>
      <Header
        style={{
          width: '100%',
          minHeight: 65,
          lineHeight: 0,
        }}
      >
        <Cabecalho titleHeader={titleHeader} info={info} watch={watch} />
      </Header>
      <Skeleton className="skeleton-inicio" loading={carregando} active />
      {!carregando && (
        !roles ||
        (roles &&
          typeof temPermissao === 'function' &&
          temPermissao(roles)) ? (
          <Content className="site-layout">{children}</Content>
        ) : (
          <Result
            style={{ backgroundColor: 'white', margin: 25 }}
            status="error"
            title="Permissão negada"
            subTitle="Por favor verifique se você possui permissão para acessar esta funcionalidade."
          />
        )
      )}
    </Layout>
         </Layout>;
}

PrivateLayout.propTypes = {
  titleHeader: PropTypes.node,
  info: PropTypes.node,
  children: PropTypes.node.isRequired,
  carregando: PropTypes.bool,
  systemVersion: PropTypes.shape({
    systemVersion: PropTypes.string,
  }),
  roles: PropTypes.arrayOf(PropTypes.string),
  temPermissao: PropTypes.func,
  watch: PropTypes.bool,
};

PrivateLayout.defaultProps = {
  titleHeader: null,
  info: null,
  carregando: null,
  systemVersion: null,
  roles: null,
  temPermissao: null,
  watch: false,
};

function RenderLayout({ isAuthenticated, isPrivate, children, ...props }) {
  return isPrivate ? (
    <PrivateLayout {...props}>{children}</PrivateLayout>
  ) : (
    <ContentLayout>{children}</ContentLayout>
  );
}

RenderLayout.propTypes = {
  isAuthenticated: PropTypes.bool,
  isPrivate: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

RenderLayout.defaultProps = {
  isAuthenticated: false,
  isPrivate: false,
};

function SystemVersionContent({ text, oldVersion, newVersion }) {
  return <span>
    {text}
    <br />
    <br />
    Versão atual:&nbsp;
    <b>{oldVersion}</b>
    <br />
    Nova versão:&nbsp;
    <b>{newVersion}</b>
         </span>;
}

SystemVersionContent.propTypes = {
  text: PropTypes.string,
  oldVersion: PropTypes.string,
  newVersion: PropTypes.string,
};

SystemVersionContent.defaultProps = {
  text: null,
  oldVersion: null,
  newVersion: null,
};

function SystemVersionTitle({ title }) {
  return <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    {title}
         </div>;
}

SystemVersionTitle.propTypes = {
  title: PropTypes.string,
};

SystemVersionTitle.defaultProps = {
  title: null,
};

function Route({
  isPrivate = false,
  component: Component,
  mediaQuerys,
  titleHeader,
  info,
  roles,
  watch,
  ...rest
}) {
  const history = useHistory();
  const { isAuthenticated, carregandoPermissoes, temPermissao } = useAuth();
  const [systemVersionState] = useState({});
  const systemVersionChecked = useRef(false);
  
  useEffect(() => {
    async function systemVersion() {
      const expiry_date_refresh_token = localStorage.getItem(
        '@TmovGR:expiry_date_refresh_token'
      );
      if (
        expiry_date_refresh_token &&
        isBefore(new Date(expiry_date_refresh_token), Date.now())
      ) {
        localStorage.clear();
        history.push('/');
        return notification.warn({
          message: 'Sessão expirada',
          description: 'Entre novamente por favor.',
        });
      }
    }

    if (!systemVersionChecked.current) {
      systemVersion();
      window.scrollTo(0, 0);
    }
  }, [Component, history]);

  useEffect(() => {
    if (!isAuthenticated && isPrivate) {
      history.push('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, isAuthenticated]);

  return (
    <ReactDOMRoute
      {...rest}
      render={props => {
        pageView(props.location.pathname);

        return (
          <RenderLayout
            {...props}
            isAuthenticated={isAuthenticated}
            titleHeader={titleHeader}
            info={info}
            systemVersion={systemVersionState}
            carregando={carregandoPermissoes}
            temPermissao={temPermissao}
            roles={roles}
            watch={watch}
            isPrivate={isPrivate}
          >
            <Component {...props} mediaQuerys={mediaQuerys} />
          </RenderLayout>
        );
      }}
    />
  );
}

Route.propTypes = {
  isPrivate: PropTypes.bool,
  component: PropTypes.elementType.isRequired,
  mediaQuerys: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
    xxl: PropTypes.bool,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  titleHeader: PropTypes.node,
  info: PropTypes.node,
  roles: PropTypes.arrayOf(PropTypes.string),
  watch: PropTypes.bool,
};

Route.defaultProps = {
  isPrivate: false,
  mediaQuerys: null,
  location: {
    pathname: null,
  },
  titleHeader: null,
  info: null,
  roles: null,
  watch: false,
};

export default Route;
